import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { priceTiers } from "@/data/pricing";

export interface DiscountRow {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  discount_type: "percent" | "fixed_amount";
  value: number;
  applies_to: "all" | "purchase" | "subscription";
  duration: "once" | "forever";
  min_tubes: number | null;
  allowed_tiers: number[] | null;
  max_redemptions: number | null;
  times_redeemed: number;
  expires_at: string | null;
  active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: DiscountRow | null;
  onSaved: () => void;
}

const emptyForm = {
  code: "",
  name: "",
  description: "",
  discount_type: "percent" as "percent" | "fixed_amount",
  value: 10,
  applies_to: "all" as "all" | "purchase" | "subscription",
  duration: "once" as "once" | "forever",
  min_tubes: "",
  allowed_tiers: [] as number[],
  max_redemptions: "",
  expires_at: "",
  active: true,
};

const DiscountFormSheet = ({ open, onOpenChange, initial, onSaved }: Props) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        code: initial.code ?? "",
        name: initial.name,
        description: initial.description ?? "",
        discount_type: initial.discount_type,
        value: initial.discount_type === "fixed_amount"
          ? Math.round(initial.value / 100)
          : initial.value,
        applies_to: initial.applies_to,
        duration: initial.duration,
        min_tubes: initial.min_tubes?.toString() ?? "",
        allowed_tiers: initial.allowed_tiers ?? [],
        max_redemptions: initial.max_redemptions?.toString() ?? "",
        expires_at: initial.expires_at ? initial.expires_at.slice(0, 10) : "",
        active: initial.active,
      });
    } else {
      setForm(emptyForm);
    }
  }, [initial, open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Namn krävs");
      return;
    }
    if (form.code && !/^[A-Z0-9_-]+$/.test(form.code.toUpperCase())) {
      toast.error("Kod får endast innehålla A-Z, 0-9, _ och -");
      return;
    }
    if (form.discount_type === "percent" && (form.value < 1 || form.value > 100)) {
      toast.error("Procent måste vara 1-100");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code ? form.code.trim().toUpperCase() : null,
        name: form.name.trim(),
        description: form.description.trim() || null,
        discount_type: form.discount_type,
        // value stored in cents/öre for fixed_amount, raw % for percent
        value: form.discount_type === "fixed_amount"
          ? Math.round(form.value * 100)
          : form.value,
        applies_to: form.applies_to,
        duration: form.duration,
        min_tubes: form.min_tubes ? parseInt(form.min_tubes, 10) : null,
        allowed_tiers: form.allowed_tiers.length > 0 ? form.allowed_tiers : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        active: form.active,
      };

      const { data: saved, error } = initial
        ? await (supabase.from as any)("discounts").update(payload).eq("id", initial.id).select("id").single()
        : await (supabase.from as any)("discounts").insert(payload).select("id").single();

      if (error) throw error;

      // Mirror the discount to Stripe (creates/updates/removes the coupon and
      // swaps it on any subscription already using this discount).
      const savedId = saved?.id ?? initial?.id;
      if (savedId) {
        const { error: syncErr } = await supabase.functions.invoke("sync-discount-stripe", {
          body: { discount_id: savedId, mode: "upsert" },
        });
        if (syncErr) {
          console.error(syncErr);
          toast.warning("Rabatten sparades, men kunde inte synkas till betalleverantören.");
        }
      }
      toast.success(initial ? "Rabatt uppdaterad" : "Rabatt skapad");
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Något gick fel");
    } finally {
      setSaving(false);
    }
  };

  const toggleTier = (tubes: number) => {
    setForm((f) => ({
      ...f,
      allowed_tiers: f.allowed_tiers.includes(tubes)
        ? f.allowed_tiers.filter((t) => t !== tubes)
        : [...f.allowed_tiers, tubes],
    }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{initial ? "Redigera rabatt" : "Ny rabatt"}</SheetTitle>
          <SheetDescription>
            Skapa en kod för engångsrabatt eller lämna kod tom för automatisk VIP-rabatt
            (kopplas senare till specifika kunder).
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-6">
          <div>
            <Label htmlFor="name">Internt namn *</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sommarkampanj 2026"
            />
          </div>

          <div>
            <Label htmlFor="code">Rabattkod (lämna tom för VIP)</Label>
            <Input
              id="code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="SOMMAR15"
              className="uppercase"
            />
          </div>

          <div>
            <Label htmlFor="description">Beskrivning (visas internt)</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rabattyp</Label>
              <Select
                value={form.discount_type}
                onValueChange={(v: any) => setForm({ ...form, discount_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Procent</SelectItem>
                  <SelectItem value="fixed_amount">Fast belopp (kr)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Värde {form.discount_type === "percent" ? "(%)" : "(kr)"}</Label>
              <Input
                type="number"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: parseInt(e.target.value) || 0 })}
                min={1}
                max={form.discount_type === "percent" ? 100 : undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gäller</Label>
              <Select
                value={form.applies_to}
                onValueChange={(v: any) => setForm({ ...form, applies_to: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla</SelectItem>
                  <SelectItem value="purchase">Endast köp</SelectItem>
                  <SelectItem value="subscription">Endast prenumeration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Varaktighet (prenumeration)</Label>
              <Select
                value={form.duration}
                onValueChange={(v: any) => setForm({ ...form, duration: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Endast första månaden</SelectItem>
                  <SelectItem value="forever">Hela perioden</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Begränsa till paket (lämna tomt = alla)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {priceTiers.map((t) => (
                <button
                  key={t.tubes}
                  type="button"
                  onClick={() => toggleTier(t.tubes)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                    form.allowed_tiers.includes(t.tubes)
                      ? "bg-foreground text-background border-foreground"
                      : "bg-background border-input text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {t.name} ({t.tubes})
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="min_tubes">Min antal tuber</Label>
              <Input
                id="min_tubes"
                type="number"
                value={form.min_tubes}
                onChange={(e) => setForm({ ...form, min_tubes: e.target.value })}
                placeholder="(valfritt)"
              />
            </div>
            <div>
              <Label htmlFor="max_red">Max användningar</Label>
              <Input
                id="max_red"
                type="number"
                value={form.max_redemptions}
                onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                placeholder="∞"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="expires_at">Utgångsdatum (valfritt)</Label>
            <Input
              id="expires_at"
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label htmlFor="active" className="text-sm">Aktiv</Label>
              <p className="text-xs text-muted-foreground">
                Inaktiva rabatter kan inte användas
              </p>
            </div>
            <Switch
              id="active"
              checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })}
            />
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Sparar..." : initial ? "Uppdatera" : "Skapa rabatt"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default DiscountFormSheet;
